import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Target, Phone, CreditCard, BookOpen, FileText, Handshake,
  X, ArrowUp, ArrowDown, CornerDownLeft, Sparkles, Loader2,
} from 'lucide-react';
import api from '../api';

const TYPE_ICON = { task: Target, call: Phone, payment: CreditCard, reading: BookOpen, note: FileText, commitment: Handshake, inbox: FileText };
const TYPE_LABEL = { task: 'Task', call: 'Call', payment: 'Payment', reading: 'Reading', note: 'Note', commitment: 'Commitment', inbox: 'Inbox' };

const SEMANTIC_DEBOUNCE_MS = 600;   // Slower — LLM is the bottleneck
const KEYWORD_DEBOUNCE_MS = 250;

export default function SearchOverlay({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [interpretation, setInterpretation] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [smartMode, setSmartMode] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Keep smartMode sticky across opens — power users shouldn't have
      // to re-toggle their preference every time they reopen the overlay.
      setQuery('');
      setResults([]);
      setInterpretation('');
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const doSearch = useCallback(async (q, smart) => {
    if (!q.trim()) { setResults([]); setInterpretation(''); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    try {
      if (smart) {
        const res = await api.post('/search/semantic', { q }, { signal: ctl.signal });
        setResults(res.data.results || []);
        setInterpretation(res.data.interpretation || '');
      } else {
        const res = await api.get('/search', { params: { q }, signal: ctl.signal });
        setResults(res.data.results || []);
        setInterpretation('');
      }
      setSelectedIdx(0);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    const delay = smartMode ? SEMANTIC_DEBOUNCE_MS : KEYWORD_DEBOUNCE_MS;
    debounceRef.current = setTimeout(() => doSearch(val, smartMode), delay);
  };

  // Re-run the active query when the user flips the Smart toggle so
  // they immediately see the LLM's ranking (vs requiring a new keystroke).
  const handleToggleSmart = () => {
    const next = !smartMode;
    setSmartMode(next);
    if (query.trim()) {
      clearTimeout(debounceRef.current);
      doSearch(query, next);
    }
  };

  const handleSelect = (result) => {
    onClose();
    if (result.path) navigate(result.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[selectedIdx]) { handleSelect(results[selectedIdx]); }
  };

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-[300] flex items-start justify-center pt-[15vh]"
      style={{ background: 'rgba(5,5,7,0.62)', backdropFilter: 'blur(36px) saturate(140%)', WebkitBackdropFilter: 'blur(36px) saturate(140%)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-[620px] mx-4 rounded-[24px] overflow-hidden"
        style={{ background: 'var(--pd-surface-3)', border: '1px solid var(--pd-border-highlight)', boxShadow: 'var(--pd-shadow-command), var(--pd-edge-highlight)', backdropFilter: 'blur(28px) saturate(160%)', WebkitBackdropFilter: 'blur(28px) saturate(160%)', animation: 'pd-modal-rise 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>

        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--pd-border)' }}>
          <Search className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
          <input ref={inputRef} data-testid="search-input"
            value={query} onChange={(e) => handleInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={smartMode ? 'Describe what you need — "things I owe Sam this week"' : 'Search tasks, calls, payments, reading, notes...'}
            className="flex-1 text-[16px] font-medium outline-none bg-transparent"
            style={{ color: 'var(--pd-text)' }} />

          <button
            type="button"
            data-testid="search-smart-toggle"
            onClick={handleToggleSmart}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold flex-shrink-0 transition-colors"
            style={{
              background: smartMode ? 'var(--pd-accent)' : 'var(--pd-panel-2)',
              color: smartMode ? 'var(--pd-canvas)' : 'var(--pd-subtle)',
              border: '1px solid ' + (smartMode ? 'var(--pd-accent)' : 'var(--pd-border)'),
            }}
            title="Toggle AI semantic search"
            aria-pressed={smartMode}
          >
            <Sparkles className="w-3 h-3" />
            <span>{smartMode ? 'Smart on' : 'Smart'}</span>
          </button>

          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-subtle)' }}>ESC</span>
          </div>
          <button onClick={onClose} className="pd-icon-button"><X className="w-4 h-4" /></button>
        </div>

        {/* Smart-mode interpretation banner — shows the LLM's read of
            the query so the user can see WHY a result was picked. */}
        {smartMode && interpretation && !loading ? (
          <div
            data-testid="search-interpretation"
            className="px-5 py-2.5 text-[11px] flex items-start gap-2"
            style={{ background: 'var(--pd-panel-2)', borderBottom: '1px solid var(--pd-border)', color: 'var(--pd-subtle)' }}
          >
            <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--pd-accent)' }} />
            <span>{interpretation}</span>
          </div>
        ) : null}

        <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
          {loading && (
            <div className="px-5 py-4 space-y-2" data-testid="search-loading">
              {smartMode ? (
                <div className="flex items-center justify-center gap-2 py-6 text-[12px]" style={{ color: 'var(--pd-subtle)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Asking AI to rank your stuff…</span>
                </div>
              ) : (
                [1, 2, 3].map(i => <div key={i} className="h-[44px] rounded-xl skeleton-shimmer" />)
              )}
            </div>
          )}

          {!loading && query && results.length === 0 && (
            <div className="px-5 py-8 text-center">
              <p className="text-[13px]" style={{ color: 'var(--pd-subtle)' }}>No results for &quot;{query}&quot;</p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="py-2">
              {results.map((result, idx) => {
                const Icon = TYPE_ICON[result.type] || Target;
                const isSelected = idx === selectedIdx;
                return (
                  <button key={`${result.type}-${result.id}`}
                    data-testid={`search-result-${result.id}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left transition-colors"
                    style={{ background: isSelected ? 'var(--pd-panel-2)' : 'transparent' }}>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: result.businessColor ? `${result.businessColor}12` : 'var(--pd-panel-2)' }}>
                      <Icon className="w-4 h-4" style={{ color: result.businessColor || 'var(--pd-subtle)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate" style={{ color: 'var(--pd-text)' }}>
                        {result.title}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[9px] uppercase tracking-wider font-semibold"
                          style={{ color: 'var(--pd-subtle)' }}>{TYPE_LABEL[result.type]}</span>
                        {result.subtitle && (
                          <>
                            <span className="text-[9px]" style={{ color: 'var(--pd-subtle)' }}>·</span>
                            <span className="text-[10px] truncate" style={{ color: 'var(--pd-muted)' }}>{result.subtitle}</span>
                          </>
                        )}
                        {result.businessShortCode && (
                          <span className="text-[8px] px-1.5 py-[1px] rounded-full font-semibold ml-auto"
                            style={{ background: `${result.businessColor}15`, color: result.businessColor }}>
                            {result.businessShortCode}
                          </span>
                        )}
                      </div>
                      {/* Smart-mode reasoning line — appears only when the
                          backend returned an LLM-provided `reason`. Keeps
                          keyword-mode visually unchanged. */}
                      {result.reason ? (
                        <div
                          data-testid={`search-reason-${result.id}`}
                          className="text-[10px] mt-1 italic"
                          style={{ color: 'var(--pd-muted)' }}
                        >
                          {result.reason}
                        </div>
                      ) : null}
                    </div>
                    {isSelected && <CornerDownLeft className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />}
                  </button>
                );
              })}
            </div>
          )}

          {!query && (
            <div className="px-5 py-6 text-center">
              <p className="text-[12px] mb-2" style={{ color: 'var(--pd-subtle)' }}>
                {smartMode ? 'Describe what you\'re looking for in plain English' : 'Search across all your data'}
              </p>
              <div className="flex items-center justify-center gap-3 text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
                <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> Navigate</span>
                <span className="flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> Select</span>
                <span>ESC Close</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
